<template>
  <div>
    <v-row>
      <v-col cols="12" md="6">
        <gg-card-title class="pb-0 pt-0">Applications</gg-card-title>
      </v-col>
      <v-col cols="12" md="6" class="text-md-right pb-2">
        <gg-btn tile color="primary" class="mt-1" :to="{ name: 'applications_deploy' }">
          Deploy new app
        </gg-btn>
      </v-col>
    </v-row>

    <applications-operations @done="onOperationDone" />

    <div v-if="loading" class="py-10 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="36" width="3" aria-hidden="true" />
      <div class="apps__muted mt-2">Finding your apps…</div>
    </div>

    <gg-card v-else-if="!servers.length">
      <gg-card-text class="py-10 text-center">
        <v-icon size="48" color="primary" aria-hidden="true">mdi-docker</v-icon>
        <h2 class="body-1 font-weight-medium mt-2 mb-1">No apps yet</h2>
        <p class="apps__muted mb-4">
          Install apps like WordPress, Uptime Kuma or Gitea in a few clicks. If you don't have a server
          for them yet, we'll set one up with Docker as part of your first app.
        </p>
        <gg-btn tile color="primary" :to="{ name: 'applications_deploy' }">
          Deploy your first app
        </gg-btn>
      </gg-card-text>
    </gg-card>

    <!-- One section per Docker server. -->
    <gg-card v-for="s in servers" :key="s.id" class="mb-4" tag="section" :aria-label="'Apps on ' + s.name">
      <gg-card-header>
        <v-row>
          <v-col md="6">
            <h2 class="apps__server-name">{{ s.name }}</h2>
            <div class="caption">{{ [s.ip, s.location].filter(v => v && v !== '—').join(' · ') }}</div>
          </v-col>
          <v-col md="6" class="d-flex flex-sm-row flex-column justify-end align-start gap-sm align-md-center">
            <gg-btn secondary class="mr-1" :to="{ name: 'hosting_kvm_info', params: { hostingid: s.id } }">Open server</gg-btn>
            <gg-btn :disabled="!!unavailable(s)" :to="{ name: 'applications_deploy', query: { server: s.id } }">
              Deploy app here
            </gg-btn>
          </v-col>
        </v-row>
      </gg-card-header>
      <v-card-text>

        <!-- The app chosen with a new server, installed once it's built. -->
        <v-alert v-if="installing(s)" type="info" dense text class="mb-2 apps__alert" role="status">
          Installing {{ s.install.name }}… it was chosen with this server and is installed automatically once the server is ready.
        </v-alert>
        <v-alert v-else-if="s.install && s.install.state === 'failed'" type="warning" dense text class="mb-2 apps__alert">
          {{ s.install.error || `${s.install.name} couldn't be installed.` }}
        </v-alert>

        <p v-if="unavailable(s)" class="apps__muted mb-0">{{ unavailable(s) }}</p>
        <div v-else-if="apps[s.id] && apps[s.id].loading && !apps[s.id].rows" class="py-4 text-center" role="status">
          <v-progress-circular indeterminate color="primary" size="24" width="2" aria-hidden="true" />
          <span class="apps__sr">Loading apps…</span>
        </div>
        <v-alert v-else-if="apps[s.id] && apps[s.id].error" type="warning" dense text class="mb-0 apps__alert">
          {{ apps[s.id].error }}
          <gg-btn x-small text class="ml-2" @click="loadApps(s)">Try again</gg-btn>
        </v-alert>
        <p v-else-if="apps[s.id] && apps[s.id].rows && !apps[s.id].rows.length" class="apps__muted mb-0">No apps on this server yet.</p>

        <v-data-table v-else-if="apps[s.id] && apps[s.id].rows" :headers="headers" :items="apps[s.id].rows"
                      item-key="name" hide-default-footer disable-pagination>
          <template v-slot:item.name="{ item: a }">
            <div class="d-flex align-center">
              <kvm-logo :item="a.app || { slug: a.name }" :size="22" default-icon="mdi-docker" class="mr-2" />
              <div>
                <router-link :to="{ name: 'applications_app', params: { serviceId: s.id, app: a.name } }" class="ggs-item-title">
                  {{ (a.app && a.app.name) || a.name }}
                </router-link>
                <div v-if="a.app && a.app.name && a.app.name !== a.name" class="caption">{{ a.name }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item.status="{ item: a }">
            <v-icon small class="pb-1" :color="statusColor(a.status)" aria-hidden="true">mdi-circle</v-icon>
            <span class="pl-1">{{ statusText(a.status) }}</span>
          </template>
          <template v-slot:item.address="{ item: a }">
            <a v-if="a.app && a.app.url" :href="a.app.url" target="_blank" rel="noopener">{{ shortUrl(a.app.url) }}</a>
            <span v-else>—</span>
          </template>
          <template v-slot:item.actions="{ item: a }">
            <gg-btn class="mr-1" :aria-label="'Manage ' + ((a.app && a.app.name) || a.name)"
                    :to="{ name: 'applications_app', params: { serviceId: s.id, app: a.name } }">
              Manage
            </gg-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import kvm from '@/api/kvm'
import { setKvmFeature } from '@/helpers/kvmFeatures'
import { kvmServices, dockerServerInfo, dockerServerUnavailable } from '@/helpers/dockerServers'
import ApplicationsOperations from '@/views/applications/ApplicationsOperations'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'

// While a new server is installing the app chosen with it, check back this often.
const INSTALL_POLL_MS = 10000

/**
 * Applications: the Docker apps on every one of the customer's Docker-host
 * servers, grouped by server. Each server is asked separately (its own token),
 * so sections fill in independently and one unreachable server doesn't hide the
 * others.
 */
export default {
  name: 'ApplicationsList',
  components: { ApplicationsOperations, KvmLogo },
  data: () => ({
    loading: true,
    servers: [],
    // service id → { loading, error, rows }
    apps: {},
    pollTimer: null,
    headers: [
      { text: 'App', value: 'name' },
      { text: 'Status', value: 'status' },
      { text: 'Address', value: 'address', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    ...mapGetters(['services', 'servicesPopulated']),
    kvmIds () {
      // Wait for the services list: before it loads there'd seem to be no servers.
      return this.servicesPopulated ? kvmServices(this.services).map(s => String(s.id)).join(',') : null
    },
  },
  watch: {
    // The services list may arrive after the page (or change after an order).
    kvmIds: { immediate: true, handler () { this.load() } },
  },
  beforeDestroy () {
    clearTimeout(this.pollTimer)
  },
  methods: {
    unavailable: dockerServerUnavailable,
    installing (s) {
      return !!(s.install && ['pending', 'installing'].includes(s.install.state))
    },
    async load () {
      if (this.kvmIds === null) return
      const list = kvmServices(this.services)
      const found = await Promise.all(list.map(svc => dockerServerInfo(svc).catch(() => null)))
      this.servers = found.filter(Boolean)
      const loads = []
      for (const s of this.servers) {
        setKvmFeature('docker', s.id, s.docker)
        if (!this.unavailable(s)) loads.push(this.loadApps(s))
      }
      this.schedulePoll()
      // The first time, wait for every server's apps: with none at all, this
      // page has nothing to show — go straight to deploying the first one.
      if (this.loading) {
        await Promise.all(loads)
        if (this.hasNoApps()) {
          this.$router.replace({ name: 'applications_deploy', query: { first: '1' } }).catch(() => {})
          return
        }
      }
      this.loading = false
    },
    // Sure there are none: every server answered with an empty list, and none is
    // installing (or failed to install) an app chosen with it. A server that's
    // off or unreachable might have apps, so it keeps the list.
    hasNoApps () {
      return this.servers.every(s => !this.unavailable(s) && !s.install &&
        this.apps[s.id] && Array.isArray(this.apps[s.id].rows) && !this.apps[s.id].rows.length)
    },
    async loadApps (s) {
      this.$set(this.apps, s.id, { ...(this.apps[s.id] || {}), loading: true, error: '' })
      try {
        const rows = (await kvm.dockerStacks(s.id)).filter(r => !r.system)
        this.$set(this.apps, s.id, { loading: false, error: '', rows })
      } catch (e) {
        this.$set(this.apps, s.id, { loading: false, error: e.message, rows: null })
      }
    },
    // A new server still setting up its app: refresh just that one until it's done.
    schedulePoll () {
      clearTimeout(this.pollTimer)
      if (!this.servers.some(s => this.installing(s) || s.building)) return
      this.pollTimer = setTimeout(async () => {
        const bySvc = Object.fromEntries(kvmServices(this.services).map(svc => [String(svc.id), svc]))
        for (const [i, s] of this.servers.entries()) {
          if (!(this.installing(s) || s.building) || !bySvc[s.id]) continue
          const fresh = await dockerServerInfo(bySvc[s.id]).catch(() => null)
          if (!fresh) continue
          this.$set(this.servers, i, fresh)
          if (!this.unavailable(fresh) && (!this.installing(fresh) || !this.apps[s.id])) this.loadApps(fresh)
        }
        this.schedulePoll()
      }, INSTALL_POLL_MS)
    },
    onOperationDone ({ serviceId }) {
      const s = this.servers.find(x => x.id === String(serviceId))
      if (s) this.loadApps(s)
    },
    statusText (status) {
      const st = String(status || '')
      if (/^running/.test(st)) return 'Running'
      if (/^exited|^stopped/.test(st)) return 'Stopped'
      if (/^restarting/.test(st)) return 'Restarting'
      if (/^created/.test(st)) return 'Starting'
      if (st === 'not deployed') return 'Not deployed'
      return st || '—'
    },
    statusColor (status) {
      const st = String(status || '')
      if (/^running/.test(st)) return 'primary'
      if (/^restarting|^created/.test(st)) return 'warning'
      return 'secondary'
    },
    shortUrl (url) { return String(url).replace(/^https?:\/\//i, '').replace(/\/$/, '') },
  },
}
</script>

<style scoped lang="scss">
@import '../services/hosting/kvm/kvm-ui';

.apps__sr { @include kvm-sr-only; }
.apps__muted { @include kvm-secondary-text; }
.apps__server-name { margin: 0; }
// `text` alerts print the message in the type colour on a faint tint (fails AA);
// near-black / near-white body text instead, as on the order page.
.apps__alert ::v-deep .v-alert__content { color: #212121 !important; }
.theme--dark .apps__alert ::v-deep .v-alert__content { color: #eceff1 !important; }
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
